import * as React from "react";
import { Link } from "react-router-dom"
import ChangePasswordInput from "../../core/ChangePasswordInput/ChangePasswordInput";
import Spinner from "../../core/Spinner/Spinner";
import debounce from "../../helpers/debounce";
import { ITranslationProps, translateComponent } from "../../helpers/translation";
import IPasswordIsValidResponse from "../../types/IPasswordIsValidResponse";
import IPasswordRequirement from "../../types/IPasswordRequirement";
import IRegisterResponse from "../../types/IRegisterResponse";
import IResponse from "../../types/IResponse";
import IValidateRegistrationKeyResponse from "../../types/IValidateRegisterKeyResponse";

enum ProcessStateValues {
    validating,
    register,
    registerComplete,
    alreadyRegistered,
    codeInvalid,
    error
}

interface IRegisterProps extends ITranslationProps {
    // onSubmit: (email: string) => void;
    validateRegistrationCode: () => Promise<IResponse<IValidateRegistrationKeyResponse> | null>;
    registerUser: (password: string, confirmPassword: string, email: string, registrationCode: string) => Promise<IResponse<IRegisterResponse> | null>;
    validatePasswordRequirements(password: string): Promise<IResponse<IPasswordIsValidResponse> | null>;
}

interface IRegisterState {
    // state control
    processState: ProcessStateValues,
    processing?: boolean;
    validated: boolean,

    // response from validating the key
    passwordRequirements: IPasswordRequirement[],
    firstName: string,
    email: string,
    registrationCode: string,

    // values entered by the user
    password: string,
    confirmPassword: string;
    confirmationEmail: string,

    // error messages
    isEmailInvalid: boolean,
    isPasswordInvalid: boolean,
    isConfirmPasswordInvalid: boolean;
    areFieldInvalid: boolean;
    isRegistrationFailure: boolean,
}

class Register extends React.Component<IRegisterProps, IRegisterState> {
    private formElement: HTMLFormElement | null;

    // We are making an API call to verify the password is correct.  But we don't want to make
    // this call on every key press.  So we are using a debounce function to only make the call
    // after the user has stopped typing for a certain period of time.
    // https://www.carlrippon.com/search-after-stop-typing-react-component/
    private validatePasswordWhenUserStoppedTyping = debounce(
        async () => {
            // call the web service to see if the password meets the requirements
            // update the display to show the user which requirements are not met
            const passwordRequirements = await this.props.validatePasswordRequirements(this.state.password);
            if (passwordRequirements && passwordRequirements.success) {
                this.setState({ passwordRequirements: passwordRequirements.payload.requirements });
            }
        },
        500);

    constructor(props: IRegisterProps) {
        super(props);

        this.state = {
            processState: ProcessStateValues.validating,
            processing: false,
            validated: false,
            passwordRequirements: [],
            firstName: "",
            email: "",
            registrationCode: "",
            password: "",
            confirmPassword: "",
            confirmationEmail: "",
            isEmailInvalid: false,
            isPasswordInvalid: false,
            isConfirmPasswordInvalid: false,
            areFieldInvalid: false,
            isRegistrationFailure: false,
        };

        // From the ChangePasswordInput control
        this.onChangePasswordInputSubmit = this.onChangePasswordInputSubmit.bind(this);
        this.onPasswordChanged = this.onPasswordChanged.bind(this);

        // Local events
        this.onEmailChange = this.onEmailChange.bind(this);
        this.onPasswordChanged = this.onPasswordChanged.bind(this);
        this.inputKeypress = this.inputKeypress.bind(this);
        this.onSubmitRegistration = this.onSubmitRegistration.bind(this);
    }

    public render() {
        return (
            <div>
                {this.state.processing && <Spinner />}
                <h2>{this.props.t("Register.Title")}</h2>
                {this.getContent()}
            </div>
        );
    }

    public async componentDidMount() {
        this.setState({ processing: true });
        const response = await this.props.validateRegistrationCode();

        let processState: ProcessStateValues;
        if (response && response.success) {
            if (response.payload.isValid) {
                this.setState({
                    passwordRequirements: response.payload.passwordRequirements,
                    firstName: response.payload.firstName,
                    email: response.payload.email,
                    registrationCode: response.payload.registrationCode
                });
                if (response.payload.isUserRegistered) {
                    processState = ProcessStateValues.alreadyRegistered;
                } else {
                    processState = ProcessStateValues.register;
                }
            } else {
                processState = ProcessStateValues.codeInvalid;
            }
        } else {
            processState = ProcessStateValues.error;
        }
        this.setState({ processing: false, processState });
    }

    private getContent() {
        switch (this.state.processState) {
            case ProcessStateValues.validating:
                return (
                    <div>{this.props.t("Register.WaitWhileValidating")}</div>
                );

            case ProcessStateValues.register:
                return (
                    <div>
                        <div className="mb-3 mt-3">{this.state.firstName}{this.props.t("Register.Instructions")}</div>
                        <form ref={ref => this.formElement = ref} className={this.state.validated ? "was-validated" : "needs-validation"} noValidate={true}>
                            <div className="form-group">
                                <label>{this.props.t("Register.EmailAddress")}</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    placeholder={this.props.t("Register.EmailAddress")}
                                    value={this.state.confirmationEmail}
                                    onChange={this.onEmailChange}
                                    onKeyPress={this.inputKeypress}
                                    required={true}
                                    autoFocus={true} />

                                <div className="invalid-feedback">
                                    {this.props.t("Register.EmailValidationMessage")}
                                </div>
                                {this.state.isEmailInvalid &&
                                    <div className="alert alert-warning">
                                        {this.props.t("Register.EmailIsInvalid")}
                                    </div>
                                }
                            </div>
                        </form>
                        <ChangePasswordInput
                            passwordRequirements={this.state.passwordRequirements}
                            onSubmit={this.onChangePasswordInputSubmit}
                            onPasswordChanged={this.onPasswordChanged}
                            doValidation={this.state.validated}
                        />
                        {this.state.isPasswordInvalid &&
                            <div className="alert alert-warning mt-3">
                                {this.props.t("Register.PasswordIsInvalid")}
                            </div>
                        }
                        <button type="button" className="btn btn-primary" onClick={this.onSubmitRegistration}>{this.props.t("Register.RegisterButton")}</button>
                        <div className="mt-3">
                            {this.state.areFieldInvalid &&
                                <div className="alert alert-warning">
                                    {this.props.t("Register.FieldsInvalid")}
                                </div>
                            }

                            {this.state.isRegistrationFailure &&
                                <div className="alert alert-warning">
                                    {this.props.t("Global.UnexpectedError")}
                                </div>
                            }
                        </div>
                    </div>
                );

            case ProcessStateValues.alreadyRegistered:
                return (
                    <div className="alert alert-warning">
                        {this.state.firstName}{this.props.t("Register.AlreadyRegistered")}<br /><br />
                        <Link to="/login">{this.props.t("Register.LoginLink")}</Link> <b>{this.state.email}</b><br /><br />
                        <Link to="/forgotPassword">{this.props.t("Register.ForgotPasswordLink")}</Link>
                    </div>
                );

            case ProcessStateValues.codeInvalid:
                return (
                    <div className="alert alert-warning">
                        {this.props.t("Register.InvalidCode")}<br /><br />
                        <Link to="/registerRequest">{this.props.t("Register.RequestRegistrationLink")}</Link>
                    </div>
                );

            case ProcessStateValues.error:
                return (
                    <div className="alert alert-warning">
                        {this.props.t("Global.UnexpectedError")}<br /><br />
                        <Link to="/registerRequest">{this.props.t("Register.RequestRegistrationLink")}</Link>
                    </div>
                );

            case ProcessStateValues.registerComplete:
                return (
                    <div>
                        <div>{this.props.t("Register.RegisterComplete")}</div>
                        <div>{this.props.t("Register.RegisterEmailSent")} {this.state.email}</div>
                        <div>
                            <Link to="/dashboard">{this.props.t("Global.ContinueButton")}</Link>
                        </div>
                    </div>
                );

            default:
                return;
        }
    }

    // -----------------------------------------------------------
    // Events
    // -----------------------------------------------------------

    private onEmailChange(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({ confirmationEmail: event.target.value });
    }

    private inputKeypress(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.key === "Enter") {
            event.preventDefault();
            this.onSubmitRegistration();
        }
    }

    private onSubmitRegistration(): void {
        this.setState({ validated: true, processing: true }, async () => {
            if (this.isValid()) {
                // Submit registration request
                const result = await this.props.registerUser(this.state.password, this.state.password, this.state.confirmationEmail, this.state.registrationCode);
                if (result && result.success && result.payload) {
                    if (result.payload.success) {
                        // register valid and complete
                        this.setState({ processState: ProcessStateValues.registerComplete });
                    } else {
                        this.setState({
                            isEmailInvalid: result.payload.emailInvalid,
                            isPasswordInvalid: result.payload.passwordInvalid,
                            passwordRequirements: result.payload.passwordRequirements,
                            isRegistrationFailure: false,
                            areFieldInvalid: true,
                        });
                    }
                } else {
                    this.setState({ isRegistrationFailure: true, isEmailInvalid: false, areFieldInvalid: false });
                }
            } else {
                this.setState({ areFieldInvalid: true });
            }
            this.setState({ processing: false });
        });
    }

    private isValid(): boolean {
        let isValid = true;

        if (this.formElement) {
            if (!this.formElement.checkValidity()) {
                isValid = false;
            }
        }
        if (this.state.password !== this.state.confirmPassword) {
            isValid = false;
        }

        return isValid;
    }

    // -----------------------------------------------------------
    // ChangePasswordInput control callback functions
    // -----------------------------------------------------------

    // Called if user presses enter on the ChangePasswordInput control
    private onChangePasswordInputSubmit(password: string, confirmPassword: string) {
        this.setState({ password, confirmPassword }, () => {
            this.onSubmitRegistration();
        });
    }

    private async onPasswordChanged(password: string, confirmPassword: string) {
        this.setState({ password, confirmPassword }, () => {
            this.validatePasswordWhenUserStoppedTyping();
        });
    }

}

export default translateComponent(Register);



// WEBPACK FOOTER //
// ./src/components/Register/Register.tsx